import React, { useState, useEffect } from 'react';
import { Box, Button, TextField, FormControl, InputLabel, Select, MenuItem, Typography, IconButton, Alert } from '@mui/material';
import { styled } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const BlogForm = ({ initialData, onSubmit, loading }) => {
  const [formData, setFormData] = useState({
    title: initialData?.title || '',
    content: initialData?.content || '',
    summary: initialData?.summary || '',
    category: initialData?.category || '',
    readTime: initialData?.readTime || '5',
    status: initialData?.status || 'draft'
  });
  const [image, setImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(initialData?.image || '');
  const [error, setError] = useState('');

  // Add clipboard paste handler
  useEffect(() => {
    const handlePaste = async (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      for (let item of items) {
        if (item.type.indexOf('image') !== -1) {
          e.preventDefault();
          const file = item.getAsFile();
          if (file) {
            setImage(file);
            setPreviewUrl(URL.createObjectURL(file));
          }
          break;
        }
      }
    };

    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError(''); // Clear error when user makes changes
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        setError('Image size must be less than 5MB');
        return;
      }
      setImage(file);
      setPreviewUrl(URL.createObjectURL(file));
      setError('');
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        setError('Image size must be less than 5MB');
        return;
      }
      setImage(file);
      setPreviewUrl(URL.createObjectURL(file));
      setError('');
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDeleteImage = () => {
    setImage(null);
    setPreviewUrl('');
    if (previewUrl && !previewUrl.startsWith('http')) {
      URL.revokeObjectURL(previewUrl);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.title || !formData.content) {
      setError('Title and content are required');
      return;
    }

    // Include the image in the form data
    const submitData = {
      ...formData,
      image: image // Pass the File object directly
    };

    onSubmit(submitData);
  };

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TextField
        fullWidth
        label="Title"
        name="title"
        value={formData.title}
        onChange={handleInputChange}
        required
        sx={{ mb: 2 }}
      />

      <TextField
        fullWidth
        label="Summary"
        name="summary"
        value={formData.summary}
        onChange={handleInputChange}
        multiline
        rows={2}
        sx={{ mb: 2 }}
      />

      <TextField
        fullWidth
        label="Category"
        name="category"
        value={formData.category}
        onChange={handleInputChange}
        placeholder="Enter a category"
        required
        sx={{ mb: 2 }}
      />

      <Box sx={{ mb: 2 }}>
        <FormControl fullWidth>
          <InputLabel>Status</InputLabel>
          <Select
            name="status"
            value={formData.status}
            onChange={handleInputChange}
            label="Status"
          >
            <MenuItem value="draft">Draft</MenuItem>
            <MenuItem value="published">Published</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <TextField
        fullWidth
        label="Read Time"
        name="readTime"
        type="number"
        value={formData.readTime}
        onChange={handleInputChange}
        placeholder="e.g., 5"
        InputProps={{
          endAdornment: <Typography color="text.secondary" sx={{ ml: 1 }}>minutes</Typography>
        }}
        sx={{ mb: 2 }}
      />

      <TextField
        fullWidth
        label="Content"
        name="content"
        value={formData.content}
        onChange={handleInputChange}
        multiline
        rows={10}
        required
        sx={{ mb: 2 }}
      />

      <Box
        sx={{
          border: '2px dashed #ccc',
          borderRadius: 2,
          p: 2,
          mb: 2,
          textAlign: 'center',
          cursor: 'pointer',
          '&:hover': {
            borderColor: 'primary.main'
          }
        }}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
      >
        <Button
          component="label"
          variant="contained"
          startIcon={<CloudUploadIcon />}
          sx={{ mb: previewUrl ? 2 : 0 }}
        >
          Upload Image
          <VisuallyHiddenInput 
            type="file" 
            accept="image/*"
            onChange={handleImageChange}
          />
        </Button>
        
        {previewUrl && (
          <Box sx={{ mt: 2, position: 'relative', display: 'inline-block' }}>
            <img 
              src={previewUrl} 
              alt="Preview" 
              style={{ maxWidth: '100%', maxHeight: '200px' }} 
            />
            <IconButton
              onClick={handleDeleteImage}
              sx={{
                position: 'absolute',
                top: 0,
                right: 0,
                bgcolor: 'background.paper'
              }}
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        )}
      </Box>

      <Button
        type="submit"
        variant="contained"
        color="primary"
        fullWidth
        disabled={loading}
      >
        {loading ? 'Saving...' : 'Save Blog Post'}
      </Button>
    </Box>
  );
};

export default BlogForm;
